import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Loader2, MoreHorizontal } from 'lucide-react';

import { apiClient } from '@/lib/api-client';
import type { ApiClientError, ApiSuccess } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PageHeader } from '@/components/efs/PageHeader';
import { TableToolbar } from '@/components/efs/TableToolbar';
import { MasterTable, type MasterTableColumn } from '@/components/efs/MasterTable';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

const NS = 'pricing';
const DEFAULT_PER_PAGE = 50;
const PAGE_SIZE_OPTIONS = [50, 100];
const KEY = 'price-references' as const;

const confidenceLevels = ['high', 'medium', 'low'] as const;
const currencies = ['JPY', 'USD', 'EUR'] as const;
const sourceTypes = [
  'internal_reference',
  'external_website',
  'dk_sales_reference',
  'ef_estimator_reference',
  'past_sales_result',
  'manual_input',
  'api_future',
] as const;

interface PriceReferenceDto {
  price_reference_id: string;
  material_id: string;
  material_name?: string;
  unit_price: string;
  currency: string;
  valid_from: string;
  confidence_level: string;
  approved_at: string | null;
  approved_by: string | null;
}

interface PriceReferenceListMeta {
  total: number;
  page: number;
  per_page: number;
}

const fetchPriceReferences = async (params: {
  page?: number;
  per_page?: number;
}): Promise<{ data: PriceReferenceDto[]; meta: PriceReferenceListMeta }> => {
  const res = await apiClient.get<ApiSuccess<PriceReferenceDto[], PriceReferenceListMeta>>(
    '/price-references',
    { params },
  );
  const data = res.data.data ?? [];
  const meta = res.data.meta ?? {
    total: data.length,
    page: params.page ?? 1,
    per_page: params.per_page ?? data.length,
  };
  return { data, meta };
};

type TFn = (key: string, opts?: Record<string, unknown>) => string;

const buildCreatePriceReferenceSchema = (t: TFn) =>
  z.object({
    material_id: z.string().min(1, t('efs.pricing.references.validation.material_id_required')),
    unit_price: z
      .string()
      .min(1, t('efs.pricing.references.validation.unit_price_required'))
      .regex(/^\d+(\.\d{1,3})?$/, t('efs.pricing.references.validation.number_invalid')),
    currency: z.enum(currencies),
    valid_from: z.string().min(1, t('efs.pricing.references.validation.valid_from_required')),
    valid_to: z.string().optional(),
    confidence_level: z.enum(confidenceLevels),
    version_code: z.string().min(1, t('efs.pricing.references.validation.version_code_required')),
    source_type: z.enum(sourceTypes),
    source_note: z.string().optional(),
    // A price change requires a reason.
    change_reason: z.string().min(1, t('efs.pricing.references.validation.change_reason_required')),
  });

type CreatePriceReferenceInput = z.infer<ReturnType<typeof buildCreatePriceReferenceSchema>>;

const useCreatePriceReference = () => {
  const qc = useQueryClient();
  return useMutation<PriceReferenceDto, ApiClientError, CreatePriceReferenceInput>({
    mutationFn: async (input) => {
      // Drop empty optional fields so the backend sees absent (not "") for the
      // nullable valid_to (date) / source_note columns.
      const body: Record<string, unknown> = {
        material_id: input.material_id,
        unit_price: input.unit_price,
        currency: input.currency,
        valid_from: input.valid_from,
        confidence_level: input.confidence_level,
        version_code: input.version_code,
        source_type: input.source_type,
        change_reason: input.change_reason,
      };
      if (input.valid_to) body.valid_to = input.valid_to;
      if (input.source_note) body.source_note = input.source_note;
      const res = await apiClient.post<ApiSuccess<PriceReferenceDto>>('/price-references', body);
      return res.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

const useApprovePriceReference = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: async (id) => {
      await apiClient.post(`/price-references/${id}/approve`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

const CreatePriceReferenceDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) => {
  const { t } = useTranslation(NS);
  const create = useCreatePriceReference();

  const createPriceReferenceSchema = useMemo(() => buildCreatePriceReferenceSchema(t), [t]);

  const form = useForm<CreatePriceReferenceInput>({
    resolver: zodResolver(createPriceReferenceSchema),
    defaultValues: {
      material_id: '',
      unit_price: '',
      currency: 'JPY',
      valid_from: new Date().toISOString().slice(0, 10),
      valid_to: '',
      confidence_level: 'medium',
      version_code: '',
      source_type: 'manual_input',
      source_note: '',
      change_reason: '',
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    await create.mutateAsync(values);
    form.reset();
    onOpenChange(false);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('efs.pricing.references.create.title', { defaultValue: '価格参照を追加' })}
          </DialogTitle>
          <DialogDescription>
            {t('efs.pricing.references.create.subtitle', {
              defaultValue: '新しい価格参照データを登録します',
            })}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <FormField
              control={form.control}
              name="material_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.pricing.references.fields.material_id', { defaultValue: '素材 ID' })}
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="material_id" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="unit_price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('efs.pricing.references.fields.unit_price', { defaultValue: '単価' })}
                    </FormLabel>
                    <FormControl>
                      <Input type="text" inputMode="decimal" placeholder="0.000" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('efs.pricing.references.fields.currency', { defaultValue: '通貨' })}
                    </FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {currencies.map((c) => (
                            <SelectItem key={c} value={c}>
                              {c}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="valid_from"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('efs.pricing.references.fields.valid_from', {
                        defaultValue: '有効開始日',
                      })}
                    </FormLabel>
                    <FormControl>
                      <DatePicker value={field.value ?? ''} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="valid_to"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('efs.pricing.references.fields.valid_to', { defaultValue: '有効終了日' })}
                    </FormLabel>
                    <FormControl>
                      <DatePicker value={field.value ?? ''} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="version_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('efs.pricing.references.fields.version_code', {
                        defaultValue: 'バージョンコード',
                      })}
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="V2026-04" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="source_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('efs.pricing.references.fields.source_type', {
                        defaultValue: '情報源種別',
                      })}
                    </FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {sourceTypes.map((s) => (
                            <SelectItem key={s} value={s}>
                              {t(`efs.pricing.references.source_type.${s}`, { defaultValue: s })}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="source_note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.pricing.references.fields.source_note', {
                      defaultValue: '情報源メモ',
                    })}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="change_reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.pricing.references.fields.change_reason', {
                      defaultValue: '変更理由',
                    })}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confidence_level"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.pricing.references.fields.confidence_level', {
                      defaultValue: '信頼度',
                    })}
                  </FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {confidenceLevels.map((cl) => (
                          <SelectItem key={cl} value={cl}>
                            {t(`efs.pricing.references.confidence.${cl}`, { defaultValue: cl })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={create.isPending}
              >
                {t('efs.common.actions.cancel', { defaultValue: 'キャンセル' })}
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                {t('efs.common.actions.save', { defaultValue: '保存' })}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

const PriceReferenceRowActions = ({
  row,
  canApprove,
}: {
  row: PriceReferenceDto;
  canApprove: boolean;
}) => {
  const { t } = useTranslation(NS);
  const approve = useApprovePriceReference();

  if (!canApprove || row.approved_at) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('efs.masters.actions.row_menu', { ns: 'masters' })}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onSelect={() => approve.mutate(row.price_reference_id)}
          disabled={approve.isPending}
        >
          {t('efs.pricing.references.actions.approve', { defaultValue: '承認' })}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const PriceReferencesPage = () => {
  const { t } = useTranslation(NS);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [createOpen, setCreateOpen] = useState(false);
  const user = useCurrentUser();
  const canApprove = hasPermission(user, 'pricing.manage');

  const query = useQuery<
    { data: PriceReferenceDto[]; meta: PriceReferenceListMeta },
    ApiClientError
  >({
    queryKey: [KEY, 'list', { page, per_page: perPage }],
    queryFn: () => fetchPriceReferences({ page, per_page: perPage }),
    placeholderData: (prev) => prev,
  });

  const rows = query.data?.data ?? [];
  const total = query.data?.meta?.total ?? 0;

  const columns = useMemo<MasterTableColumn<PriceReferenceDto>[]>(
    () => [
      {
        key: 'material_id',
        header: t('efs.pricing.references.fields.material_id', { defaultValue: '素材 ID' }),
        cell: (row) => (
          <span className="font-mono text-xs">{row.material_name ?? row.material_id}</span>
        ),
      },
      {
        key: 'unit_price',
        header: t('efs.pricing.references.fields.unit_price', { defaultValue: '単価' }),
        align: 'right',
        cell: (row) => (
          <span className="font-mono tabular-nums">
            {row.unit_price} {row.currency}
          </span>
        ),
      },
      {
        key: 'valid_from',
        header: t('efs.pricing.references.fields.valid_from', { defaultValue: '有効開始日' }),
        cell: (row) => row.valid_from,
      },
      {
        key: 'confidence_level',
        header: t('efs.pricing.references.fields.confidence_level', { defaultValue: '信頼度' }),
        cell: (row) => (
          <Badge variant="outline">
            {t(`efs.pricing.references.confidence.${row.confidence_level}`, {
              defaultValue: row.confidence_level,
            })}
          </Badge>
        ),
      },
      {
        key: 'approved_at',
        header: t('efs.pricing.references.fields.approved_at', { defaultValue: '承認日時' }),
        cell: (row) =>
          row.approved_at ? (
            <span className="text-xs">{new Date(row.approved_at).toLocaleDateString()}</span>
          ) : (
            <Badge variant="secondary">
              {t('efs.pricing.references.pending', { defaultValue: '未承認' })}
            </Badge>
          ),
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        cell: (row) => <PriceReferenceRowActions row={row} canApprove={canApprove} />,
      },
    ],
    [t, canApprove],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.common.nav.price_references', { defaultValue: '価格参照' })}
        subtitle={t('efs.pricing.references.subtitle', { defaultValue: '素材単価・信頼度の管理' })}
        count={total}
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1 h-4 w-4" />
            {t('efs.pricing.references.actions.create', { defaultValue: '価格参照を追加' })}
          </Button>
        }
      />
      <TableToolbar count={t('efs.masters.toolbar.count', { ns: 'masters', count: total })} />
      <MasterTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.price_reference_id}
        isLoading={query.isLoading}
        pagination={{
          page,
          perPage,
          total,
          onPageChange: setPage,
          onPerPageChange: (nextPerPage) => {
            setPage(1);
            setPerPage(nextPerPage);
          },
          pageSizeOptions: PAGE_SIZE_OPTIONS,
        }}
      />
      <CreatePriceReferenceDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
};

export default PriceReferencesPage;
